import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { getPromoData } from "../components/PromoComponents";
import { trackCompleteRegistration } from "../utils/facebookPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoApplied, setPromoApplied] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      }
    } catch (e) {
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name, referralCode = null, affiliateCode = null) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code = referralCode;
    if (affiliateCode) payload.affiliate_code = affiliateCode;
    
    // Check for active promo
    const promoData = getPromoData();
    if (promoData) {
      payload.promo_code = promoData.promo_code;
      payload.promo_started_at = promoData.promo_started_at;
    }
    
    const res = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    
    // Track Facebook Pixel conversion event
    trackCompleteRegistration('direct_signup');
    
    // Check if promo was applied
    if (res.data.promo_applied) {
      setPromoApplied({
        message: res.data.promo_message,
        bonusCredits: res.data.bonus_credits
      });
      // Clear promo from localStorage after successful application
      localStorage.removeItem('promo_started_at');
      localStorage.removeItem('promo_code');
    }
    
    return res.data;
  };

  const clearPromoApplied = () => {
    setPromoApplied(null);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {}
    localStorage.removeItem("token");
    setUser(null);
  };

  const processOAuthSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const resendVerificationEmail = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.post(`${API}/auth/resend-verification`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  };

  // Check if user needs email verification
  const needsEmailVerification = user && user.email_verified === false;

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      processOAuthSession, 
      checkAuth,
      resendVerificationEmail,
      needsEmailVerification,
      promoApplied,
      clearPromoApplied
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
